import { Component, useState, useEffect, useRef, type ErrorInfo, type ReactNode } from "react";
import { createPluginRegistration } from "@embedpdf/core";
import { EmbedPDF } from "@embedpdf/core/react";
import { usePdfiumEngine } from "@embedpdf/engines/react";

// Import the essential plugins
import {
  Viewport,
  ViewportPluginPackage,
} from "@embedpdf/plugin-viewport/react";
import { ScrollPluginPackage } from "@embedpdf/plugin-scroll/react";
import { LoaderPluginPackage } from "@embedpdf/plugin-loader/react";
import {
  RenderLayer,
  RenderPluginPackage,
} from "@embedpdf/plugin-render/react";
import { ZoomPluginPackage, useZoom } from "@embedpdf/plugin-zoom/react";

export interface PDFHighlight {
  id: string;
  page: number; // 1-based page number
  boundingBox: {
    x: number; // Percentage from left (0-100)
    y: number; // Percentage from top (0-100)
    width: number; // Percentage width (0-100)
    height: number; // Percentage height (0-100)
  };
  color?: string;
  label?: string;
  sourceText?: string;
}

interface EmbeddablePDFViewerProps {
  pdfUrl?: string;
  height?: string;
  showAnnotations?: boolean;
  highlights?: PDFHighlight[];
  initialPage?: number;
}

interface PDFToolbarProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

// Toolbar component that uses zoom controls (must be inside EmbedPDF provider)
const PDFToolbar = ({
  currentPage,
  totalPages,
  onPageChange,
}: PDFToolbarProps) => {
  const zoom = useZoom();

  const handleZoomIn = () => {
    try {
      if (zoom?.provides) {
        zoom.provides.zoomIn();
      }
    } catch (error) {
      console.warn("Zoom in failed:", error);
    }
  };

  const handleZoomOut = () => {
    try {
      if (zoom?.provides) {
        zoom.provides.zoomOut();
      }
    } catch (error) {
      console.warn("Zoom out failed:", error);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirstPage = () => {
    onPageChange(1);
  };

  const handleLastPage = () => {
    onPageChange(totalPages);
  };

  return (
    <div className="flex items-center justify-between bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
      {/* Left side: Page Navigation */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Page
        </span>

        <div className="flex items-center gap-1">
          <button
            onClick={handleFirstPage}
            disabled={currentPage === 1}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="First Page"
            data-testid="first-page-button"
          >
            <svg
              className="w-4 h-4 text-gray-700 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Previous Page"
            data-testid="prev-page-button"
          >
            <svg
              className="w-4 h-4 text-gray-700 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="flex items-center gap-2 px-3">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[4rem] text-center">
              {currentPage} / {totalPages}
            </span>
          </div>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Next Page"
            data-testid="next-page-button"
          >
            <svg
              className="w-4 h-4 text-gray-700 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <button
            onClick={handleLastPage}
            disabled={currentPage === totalPages}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Last Page"
            data-testid="last-page-button"
          >
            <svg
              className="w-4 h-4 text-gray-700 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Right side: Zoom Controls */}
      <div className="flex items-center gap-2">
        {zoom?.provides && (
          <div className="flex items-center gap-1">
            <button
              onClick={handleZoomOut}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Zoom Out"
              data-testid="zoom-out-button"
            >
              <svg
                className="w-4 h-4 text-gray-700 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                />
              </svg>
            </button>
            <button
              onClick={handleZoomIn}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Zoom In"
              data-testid="zoom-in-button"
            >
              <svg
                className="w-4 h-4 text-gray-700 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Error Boundary to catch PDF loading errors
interface ErrorBoundaryProps {
  children: ReactNode;
  height: string;
  pdfUrl: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class PDFErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("PDF Viewer Error:", error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state when PDF URL changes
    if (prevProps.pdfUrl !== this.props.pdfUrl && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex items-center justify-center"
          style={{ height: this.props.height }}
          data-testid="pdf-viewer-error"
        >
          <div className="text-center max-w-md p-6">
            <div className="text-red-500 dark:text-red-400 text-4xl mb-4">
              ⚠️
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Failed to Load PDF
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {this.state.error?.message || "Unable to load the PDF document"}
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-left mb-4">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Common causes:
              </p>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                <li>CORS restrictions from the PDF host</li>
                <li>Invalid or inaccessible PDF URL</li>
                <li>Network connectivity issues</li>
                <li>Corrupted PDF file</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Try a different PDF or ensure the URL allows cross-origin
              requests.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const EmbeddablePDFViewer = ({
  pdfUrl = "https://pdfobject.com/pdf/sample.pdf",
  height = "100%",
  showAnnotations = false,
  highlights = [],
  initialPage = 1,
}: EmbeddablePDFViewerProps) => {
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const hasLoadedRef = useRef(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const documentRef = useRef<any>(null); // Store document reference for cleanup

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      // Clear any pending timeouts
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Reset error and loading state when URL changes
  useEffect(() => {
    setLoadError(null);
    setIsDocumentLoading(true);
    setCurrentPage(initialPage); // Reset to initial page when URL changes
    hasLoadedRef.current = false;
    let errorDetected = false;

    // Set a timeout to detect if PDF fails to load (only if no error was detected)
    errorTimeoutRef.current = setTimeout(() => {
      if (!hasLoadedRef.current && !errorDetected) {
        setLoadError(
          "PDF took too long to load. This may be due to CORS restrictions or network issues."
        );
        setIsDocumentLoading(false);
      }
    }, 15000); // 15 second timeout

    // Listen for console errors related to PDF loading - be very specific
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      const errorMessage = String(args[0] || "");

      // Only catch specific PDF loading errors, not general React errors
      const isPDFError =
        errorMessage.includes("PDF loading failed") ||
        (errorMessage.includes("Access to fetch") &&
          errorMessage.includes(".pdf"));

      if (isPDFError && !hasLoadedRef.current) {
        errorDetected = true;
        setLoadError(
          "Failed to load PDF. The server may not allow cross-origin requests (CORS)."
        );
        setIsDocumentLoading(false);
      }

      originalConsoleError.apply(console, args);
    };

    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      console.error = originalConsoleError;
    };
  }, [pdfUrl, initialPage]);

  // Register the plugins
  const plugins = [
    createPluginRegistration(LoaderPluginPackage, {
      loadingOptions: {
        type: "url",
        pdfFile: {
          id: "pdf-viewer",
          url: pdfUrl,
        },
      },
    }),
    createPluginRegistration(ViewportPluginPackage),
    createPluginRegistration(ScrollPluginPackage),
    createPluginRegistration(RenderPluginPackage),
    createPluginRegistration(ZoomPluginPackage),
  ];

  // Initialize the engine with the React hook
  const { engine, isLoading } = usePdfiumEngine();

  // Load the document and get page count when engine is ready
  useEffect(() => {
    if (engine && !isLoading && pdfUrl) {
      const loadDocument = async () => {
        try {
          // Close previous document if exists to prevent memory leaks
          if (documentRef.current) {
            try {
              await engine.closeDocument(documentRef.current).toPromise();
              console.log("Closed previous document");
            } catch (err) {
              console.warn("Failed to close previous document:", err);
            }
            documentRef.current = null;
          }

          // Open the document using the engine's API to get the page count
          const document = await engine
            .openDocumentUrl({ id: "pdf-viewer-doc", url: pdfUrl })
            .toPromise();

          // Store document reference for cleanup
          documentRef.current = document;

          if (document && typeof document.pageCount === "number") {
            setTotalPages(document.pageCount);
            console.log(
              `PDF loaded successfully with ${document.pageCount} pages`
            );
          }

          // Mark as loaded and clear loading states
          hasLoadedRef.current = true;
          setIsDocumentLoading(false);
          setLoadError(null);

          // Clear the error timeout since PDF loaded successfully
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
          }
        } catch (error) {
          console.warn("Could not load document or get page count:", error);
          // Keep default page count of 1 if we can't determine it
          setIsDocumentLoading(false);
        }
      };

      loadDocument();

      // Cleanup: Close document when URL changes or component unmounts
      return () => {
        if (documentRef.current && engine) {
          engine
            .closeDocument(documentRef.current)
            .toPromise()
            .catch((err) => {
              console.warn("Failed to close document on cleanup:", err);
            });
          documentRef.current = null;
        }
      };
    }
  }, [engine, isLoading, pdfUrl]);

  // Reset to first page when PDF URL changes
  useEffect(() => {
    setCurrentPage(1);
    setTotalPages(1);
  }, [pdfUrl]);

  // Show error state if PDF failed to load
  if (loadError) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height }}
        data-testid="pdf-viewer-error"
      >
        <div className="text-center max-w-md p-6">
          <div className="text-red-500 dark:text-red-400 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Failed to Load PDF
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {loadError}
          </p>
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-left mb-4">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Common causes:
            </p>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
              <li>CORS restrictions from the PDF host</li>
              <li>Invalid or inaccessible PDF URL</li>
              <li>Network connectivity issues</li>
              <li>Corrupted PDF file</li>
            </ul>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Try a different PDF or ensure the URL allows cross-origin requests.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || !engine) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height }}
        data-testid="pdf-viewer-loading"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading PDF Engine...
          </p>
        </div>
      </div>
    );
  }

  // Handle page changes
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Wrap the UI with the <EmbedPDF> provider
  return (
    <PDFErrorBoundary height={height} pdfUrl={pdfUrl}>
      <div
        style={{ height }}
        data-testid="pdf-viewer"
        className="flex flex-col"
      >
        <EmbedPDF engine={engine} plugins={plugins}>
          <PDFToolbar
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
          <Viewport
            style={{
              backgroundColor: "var(--color-background-secondary, #f1f3f5)",
              flex: 1,
              padding: "16px",
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            {/* Single page view - render only the current page directly */}
            <div style={{ position: "relative" }}>
              {/* Get highlights for current page */}
              {(() => {
                const pageHighlights = highlights.filter(
                  (h) => h.page === currentPage
                );

                return (
                  <div
                    style={{
                      position: "relative",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    <RenderLayer pageIndex={currentPage - 1} scale={1.0} />

                    {/* Render highlights overlay */}
                    {showAnnotations &&
                      pageHighlights.map((highlight) => (
                        <div
                          key={highlight.id}
                          style={{
                            position: "absolute",
                            left: `${highlight.boundingBox.x}%`,
                            top: `${highlight.boundingBox.y}%`,
                            width: `${highlight.boundingBox.width}%`,
                            height: `${highlight.boundingBox.height}%`,
                            backgroundColor:
                              highlight.color || "rgba(255, 255, 0, 0.3)",
                            border: `2px solid ${highlight.color?.replace("0.3", "0.6") || "rgba(255, 255, 0, 0.6)"}`,
                            pointerEvents: "none",
                            transition: "opacity 0.2s",
                          }}
                          title={highlight.label || highlight.sourceText}
                        />
                      ))}
                  </div>
                );
              })()}
            </div>
          </Viewport>
        </EmbedPDF>
      </div>
    </PDFErrorBoundary>
  );
};

export default EmbeddablePDFViewer;
